// src/Pages/AddProductForm.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Row, Col, Card, Typography, Form, Input, InputNumber, Select,
  Button, Upload, message, Switch, Checkbox, Spin, Steps, Modal, Image, Table,
} from "antd";
import {
  UploadOutlined, ArrowLeftOutlined, ArrowRightOutlined, PlusOutlined, CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

// Rich text editor
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Import the ProductLivePreview component
import ProductLivePreview from "./ProductLivePreview";

message.config({ top: 80, duration: 2 });

const { Option } = Select;
const { Title, Text } = Typography;

const PRICE_STEP = 50;
const MAX_IMAGES = 12;

const AddProductForm = () => {
  const [fileList, setFileList] = useState([]);
  const [mainIndex, setMainIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSizes, setLoadingSizes] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [userRole, setUserRole] = useState("admin");

  const [isMattressSelected, setIsMattressSelected] = useState(false);
  const [packageCount, setPackageCount] = useState(1);

  const [currentStep, setCurrentStep] = useState(0);

  // Price Gap (for multi-size auto increment)
  const [priceGap, setPriceGap] = useState(0);

  // Form state for live preview
  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [sku, setSku] = useState("");
  const [stock, setStock] = useState(0);
  const [shippingAvailable, setShippingAvailable] = useState(false);

  // NEW: Per-size dimensions state (like beds)
  const [sizeDimensions, setSizeDimensions] = useState({}); // { [sizeId]: { length, width, height } }

  // default 0 => only original, 1 => one duplicate, etc.
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [markInventoryPart, setMarkInventoryPart] = useState(false);

  // Per-size pricing + cost + stock
  // shape: { [sizeId]: { price, cost, stock, _priceTouched, _costTouched } }
  const [sizePriceRows, setSizePriceRows] = useState({});

  // DRAFT states
  const [draftId, setDraftId] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftError, setDraftError] = useState(null);
const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [showDraftChoiceModal, setShowDraftChoiceModal] = useState(false);
const [latestDraft, setLatestDraft] = useState(null);
const [checkingLatestDraft, setCheckingLatestDraft] = useState(true);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // debounce refs and save control
  const saveTimerRef = useRef(null);
  const lastSavedSnapshotRef = useRef(null);
  const isMountedRef = useRef(false);

  // Prevent concurrent saves creating duplicate records
  const saveInProgressRef = useRef(false);
  const savePendingRef = useRef(null);

  // Client-side id for idempotency (sent with every save)
  const draftClientIdRef = useRef(`draft-${Date.now()}-${Math.floor(Math.random() * 100000)}`);

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (role) setUserRole(role);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; clearTimeout(saveTimerRef.current); };
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoadingCategories(true);
      setLoadingSizes(true);
      setLoadingColors(true);
      try {
        const [catRes, sizeRes, colorRes] = await Promise.all([
          axiosInstance.get("/category/categories"),
          axiosInstance.get("/product/sizes"),
          axiosInstance.get("/product/colors"),
        ]);
        setCategories(catRes.data.categories || []);
        setSizes(sizeRes.data.sizes || []);
        setColors(colorRes.data.colors || []);

        // Initialize size dimensions
        const initialDims = {};
        (sizeRes.data.sizes || []).forEach((s) => {
          initialDims[s.id] = { length: "", width: "", height: "" };
        });
        setSizeDimensions(initialDims);
      } catch {
        message.error("Error fetching initial data");
      } finally {
        setLoadingCategories(false);
        setLoadingSizes(false);
        setLoadingColors(false);
      }
    };
    fetchAll();
  }, []);

useEffect(() => {
  const checkLatestDraft = async () => {
    try {
      const res = await axiosInstance.get("/admin/drafts/latest");

      if (res.data.success && res.data.draft) {
        setLatestDraft(res.data.draft);
        setShowDraftChoiceModal(true);
      } else {
        // No draft exists → allow autosave immediately
        setAutoSaveEnabled(true);
      }
    } catch (err) {
      console.error("Failed to check latest draft:", err);
      setAutoSaveEnabled(true); // allow autosave even if API fails
    } finally {
      setCheckingLatestDraft(false);
    }
  };

  checkLatestDraft();
}, []);


  // If draftId present in URL, load draft into form


  const isMattressName = (name = "") => String(name).toLowerCase().includes("mattress");

  useEffect(() => {
    const cat = queryParams.get("category");
    const img = queryParams.get("image");
    if (cat) {
      setSelectedCategories([cat]);
      form.setFieldsValue({
        category: [cat],
      });
      const hasMattress = isMattressName(cat);
      setIsMattressSelected(hasMattress);
      if (hasMattress) {
        setPackageCount(1);
        form.setFieldsValue({ packageCount: 1 });
      }
    }
  }, [queryParams, form]);

  const generateUniqueName = (baseName, index) => {
    if (!baseName) return "";
    if (index === 0) return baseName;
    return `${baseName} - Copy ${index}`;
  };

  const generateUniqueSku = (baseSku, index) => {
    if (!baseSku) return undefined;
    if (index === 0) return baseSku;
    return `${baseSku}-${index}`;
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const beforeUploadValidate = (file) => {
    const isAllowedType =
      file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
    if (!isAllowedType) {
      message.error("Only JPG, PNG or WEBP images are allowed.");
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("Image must be smaller than 10MB.");
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview && file.originFileObj) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewSrc(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url?.substring(file.url.lastIndexOf("/") + 1));
  };

  const handleImageChange = ({ fileList: newList }) => {
    const ordered = [...newList];
    setFileList(ordered);

    // keep main index safe
    if (mainIndex >= ordered.length) {
      setMainIndex(0);
    }

    scheduleDraftSave();
  };

  const handleRemoveImage = (idx) => {
    const updated = fileList.filter((_, i) => i !== idx);
    setFileList(updated);
    if (updated.length === 0) {
      setMainIndex(0);
      scheduleDraftSave();
      return;
    }
    if (idx === mainIndex) {
      setMainIndex(0);
    } else if (idx < mainIndex) {
      setMainIndex((prev) => Math.max(prev - 1, 0));
    }
    scheduleDraftSave();
  };

  // ✅ MOVE IMAGE LEFT / RIGHT (REORDER)
  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= fileList.length) return;

    setFileList((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });

    // Keep main image index correct
    if (fromIndex === mainIndex) {
      setMainIndex(toIndex);
    } else if (fromIndex < mainIndex && toIndex >= mainIndex) {
      setMainIndex((prev) => prev - 1);
    } else if (fromIndex > mainIndex && toIndex <= mainIndex) {
      setMainIndex((prev) => prev + 1);
    }
    scheduleDraftSave();
  };

  const setMainImage = (index) => {
    setMainIndex(index);
    scheduleDraftSave();
  };

  const fileToUrl = (file) => {
    if (!file) return "";
    return file.url || file.thumbUrl || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : "");
  };

  // Keep price rows in sync with selected sizes
  useEffect(() => {
    const selectedObjs = sizes.filter((s) => selectedSizes.includes(s.size));
    setSizePriceRows((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const id = Number(k);
        if (!selectedObjs.some((s) => s.id === id)) delete next[k];
      });
      selectedObjs.forEach((s) => {
        if (!next[s.id]) {
          next[s.id] = { price: "", cost: "", stock: "", _priceTouched: false, _costTouched: false };
        }
      });
      return { ...next };
    });

    // Initialize dimensions for newly selected sizes
    setSizeDimensions((prev) => {
      const next = { ...prev };
      selectedObjs.forEach((s) => {
        if (!next[s.id]) {
          next[s.id] = { length: "", width: "", height: "" };
        }
      });
      return next;
    });

    scheduleDraftSave();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSizes, sizes]);

  const updatePriceRow = (id, patch) => {
    setSizePriceRows((prev) => ({ ...prev, [id]: { ...(prev[id] || { price: "", cost: "", stock: "" }), ...patch } }));
    scheduleDraftSave();
  };

  // Auto-fill helper from base values (uses Price Gap when multiple sizes)
  const autoFillFromBase = (saleBase, costBase) => {
    const saleNum = saleBase !== "" && saleBase != null ? Number(saleBase) : null;
    const costNum = costBase !== "" && costBase != null ? Number(costBase) : null;

    const ordered = sizes.filter((s) => selectedSizes.includes(s.size));
    if (ordered.length === 0) return;

    const gap = ordered.length > 1 ? Number(priceGap || 0) : 0;
    const fallbackGap = ordered.length > 1 ? PRICE_STEP : 0;
    const useGap = Number.isFinite(gap) ? gap : fallbackGap;

    setSizePriceRows((prev) => {
      const next = { ...prev };
      ordered.forEach((s, idx) => {
        if (!next[s.id]) {
          next[s.id] = { price: "", cost: "", stock: "", _priceTouched: false, _costTouched: false };
        }
        if (saleNum != null && !Number.isNaN(saleNum) && next[s.id]._priceTouched !== true) {
          next[s.id].price = saleNum + idx * useGap;
        }
        if (costNum != null && !Number.isNaN(costNum) && next[s.id]._costTouched !== true) {
          next[s.id].cost = costNum + idx * useGap;
        }
      });
      return { ...next };
    });
  };

  // Trigger auto-fill whenever base values, priceGap, or size selection changes
  useEffect(() => {
    autoFillFromBase(price, costPrice);
  }, [price, costPrice, selectedSizes, sizes.length, priceGap]);

  // NEW: Update dimensions for a specific size
  const updateSizeDimension = (sizeId, field, value) => {
    setSizeDimensions((prev) => ({
      ...prev,
      [sizeId]: {
        ...(prev[sizeId] || { length: "", width: "", height: "" }),
        [field]: value,
      },
    }));
    scheduleDraftSave();
  };

  // ---------- DRAFT helpers ----------
  const getFileListMeta = () => fileList.map((f) => ({
    uid: f.uid,
    name: f.name,
    url: fileToUrl(f),
    size: f.size || (f.originFileObj && f.originFileObj.size) || null,
    type: f.type || (f.originFileObj && f.originFileObj.type) || null,
  }));

  const buildDraftSnapshot = () => {
    const values = form.getFieldsValue(true);
    const snapshot = {
      values,
      selectedCategories,
      selectedSizes,
      selectedColors,
      sizeDimensions,
      sizePriceRows,
      fileListMeta: getFileListMeta(),
      mainIndex,
      savedAt: new Date().toISOString(),
    };
    return snapshot;
  };

  const scheduleDraftSave = (delay = 1500) => {
    if (!autoSaveEnabled) return;
    // clear previous timer
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    const snapshotObj = buildDraftSnapshot();
    const snapshotStr = JSON.stringify(snapshotObj);

    // if snapshot equals last saved, skip scheduling
    if (lastSavedSnapshotRef.current && lastSavedSnapshotRef.current === snapshotStr) {
      return;
    }

    // schedule actual save
    saveTimerRef.current = setTimeout(() => {
      performSave(snapshotObj);
    }, delay);
  };

  // Robust performSave: prevents concurrent POSTs and ensures draftId is set immediately
  const performSave = async (snapshotObj = null) => {
    if (!isMountedRef.current) return;
    const snapshot = snapshotObj || buildDraftSnapshot();
    const snapshotStr = JSON.stringify(snapshot);

    // if same as last saved, skip
    if (lastSavedSnapshotRef.current && lastSavedSnapshotRef.current === snapshotStr) return;

    // If a save is already in progress, keep only the latest and return (it will be processed after current save)
    if (saveInProgressRef.current) {
      savePendingRef.current = snapshot;
      return;
    }

    setDraftSaving(true);
    setDraftError(null);
    saveInProgressRef.current = true;

    try {
      const payload = {
        title: (snapshot.values && snapshot.values.productName) || (snapshot.values && snapshot.values.values?.productName) || "Untitled draft",
        data: snapshot,
        clientId: draftClientIdRef.current, // client id for idempotency on server if implemented
      };

      if (draftId) {
        const resp = await axiosInstance.put(`/admin/drafts/${draftId}`, payload);
        if (resp?.data?.success) {
          lastSavedSnapshotRef.current = snapshotStr;
          setDraftSavedAt(new Date());
          // keep draftId unchanged (server used it)
          message.success("Draft updated");
        } else {
          throw new Error(resp?.data?.message || "Failed to update draft");
        }
      } else {
        const resp = await axiosInstance.post("/admin/drafts", payload);
        if (resp?.data?.success) {
          // server should return a draft id
          const newId = resp.data.draftId || resp.data.id || resp.data._id || resp.data.data?.id || resp.data.draft?.id;
          if (newId) {
            setDraftId(newId);
            // also update query string so reloads / links carry the id (optional)
            try {
              const url = new URL(window.location.href);
              url.searchParams.set("draftId", String(newId));
              window.history.replaceState({}, "", url.toString());
            } catch (e) {
              // ignore URL replace errors
            }
          }
          lastSavedSnapshotRef.current = snapshotStr;
          setDraftSavedAt(new Date());
          message.success("Draft saved");
        } else {
          throw new Error(resp?.data?.message || "Failed to create draft");
        }
      }
    } catch (e) {
      console.error("Draft save error:", e);
      setDraftError(e.message || "Save failed");
      message.error(`Draft save failed: ${e.message || ""}`);
    } finally {
      saveInProgressRef.current = false;
      if (isMountedRef.current) setDraftSaving(false);
      // If a newer snapshot was queued while we were saving, save it now
      if (savePendingRef.current) {
        const pending = savePendingRef.current;
        savePendingRef.current = null;
        // guard to avoid infinite loop if identical
        if (JSON.stringify(pending) !== lastSavedSnapshotRef.current) {
          // save immediately (no delay)
          performSave(pending);
        }
      }
    }
  };

  const handleManualSaveDraft = async () => {
    // cancel any pending auto-save and save now
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    await performSave();
  };

  const loadDraftIntoForm = async (draftIdToLoad) => {
  try {
    const resp = await axiosInstance.get(`/admin/drafts/${draftIdToLoad}`);

    if (!resp.data.success) {
      message.error("Failed to load draft");
      return;
    }

    const draft = resp.data.draft;
    const doc =
      typeof draft.data === "string"
        ? JSON.parse(draft.data)
        : draft.data;

    if (doc.values) {
      form.setFieldsValue(doc.values);
      setProductName(doc.values.productName || "");
      setDescription(doc.values.description || "");
      setPrice(doc.values.price || "");
      setCostPrice(doc.values.factory_price || "");
      setSku(doc.values.sku || "");
      setStock(doc.values.stock || 0);
      setDuplicateCount(doc.values.duplicateCount || 0);
      setMarkInventoryPart(doc.values.markInventoryPart || false);
      setPriceGap(doc.values.priceGap || 0);
      setPackageCount(doc.values.packageCount || 1);
    }

    if (doc.selectedCategories) {
      setSelectedCategories(doc.selectedCategories);
      form.setFieldsValue({ category: doc.selectedCategories });
    }

    if (doc.selectedSizes) {
      setSelectedSizes(doc.selectedSizes);
      form.setFieldsValue({ sizes: doc.selectedSizes });
    }

    if (doc.selectedColors) {
      setSelectedColors(doc.selectedColors);
      form.setFieldsValue({ colors: doc.selectedColors });
    }

    if (doc.sizeDimensions) setSizeDimensions(doc.sizeDimensions);
    if (doc.sizePriceRows) setSizePriceRows(doc.sizePriceRows);

    if (doc.fileListMeta) {
      const meta = doc.fileListMeta.map((m) => ({
        uid: m.uid || `${m.name}-${Math.random()}`,
        name: m.name || "file",
        url: m.url || m.thumbUrl || null,
        thumbUrl: m.url || m.thumbUrl || null,
      }));
      setFileList(meta);
    }

    if (doc.mainIndex != null) setMainIndex(doc.mainIndex);

    setDraftId(draftIdToLoad);
    setShowDraftChoiceModal(false);
    message.success("Draft loaded");
  } catch (err) {
    console.error("Load draft error:", err);
    message.error("Error loading draft");
  }
};

  // ---------- FORM submit ----------
  const onFinish = async () => {
    setSubmitLoading(true);
    try {
      const values = form.getFieldsValue(true);

      if (!fileList || fileList.length === 0) {
        message.error("Please upload at least one image!");
        setSubmitLoading(false);
        return;
      }

      // 0 means only the original; 1 means one duplicate, etc.
      const duplicateCopies = Number(values.duplicateCount ?? 0);

      const categoryIds = categories
        .filter((c) => (values.category || []).includes(c.category_name))
        .map((c) => c.id);

      const sizeObjs = sizes.filter((s) => (values.sizes || []).includes(s.size));
      const sizeIds = sizeObjs.map((s) => s.id);

      const colorIds = colors
        .filter((c) => (values.colors || []).includes(c.color_name))
        .map((c) => c.id);

      const numericStock = Number(values.stock || 0);
      const derivedStatus = numericStock > 0 ? "in stock" : "out of stock";

      const finalPackageCount = Number(values.packageCount ?? packageCount ?? 1);

      // NEW: Build size dimensions payload (per-size dimensions)
      const sizeDimensionsPayload = {};
      sizeObjs.forEach((s) => {
        const d = sizeDimensions[s.id] || {};
        sizeDimensionsPayload[s.id] = {
          length: (d.length ?? "").toString().trim() || null,
          width: (d.width ?? "").toString().trim() || null,
          height: (d.height ?? "").toString().trim() || null,
        };
      });

      const priceMap = {};
      const costPriceMap = {};
      const stockMap = {};
      sizeObjs.forEach((s) => {
        const row = sizePriceRows[s.id] || {};
        if (row.price !== "" && row.price != null) {
          const n = Number(row.price);
          if (!Number.isNaN(n)) priceMap[s.id] = n;
        }
        if (userRole === "super admin" && row.cost !== "" && row.cost != null) {
          const c = Number(row.cost);
          if (!Number.isNaN(c)) costPriceMap[s.id] = c;
        }
        if (row.stock !== "" && row.stock != null) {
          const q = Number(row.stock);
          if (!Number.isNaN(q)) stockMap[s.id] = parseInt(q, 10);
        }
      });

      const createdProductIds = [];

      // include original + duplicateCopies
      for (let i = 0; i <= duplicateCopies; i++) {
        const baseName = (values.productName || "").trim();
        const baseDesc = (values.description || "").trim();

        const payload = {
          product_name: generateUniqueName(baseName, i),
          description: baseDesc ? `${baseDesc}${i > 0 ? ` (Copy ${i})` : ""}` : "",
          status: derivedStatus,
          sku: generateUniqueSku(values.sku, i),
          stock: numericStock,
          weight: values.weight || 0,
          price_euro: values.price,
          factory_price: values.factory_price || 0,
          categoryIds,
          sizeIds,
          colorIds,
          shippingAvailable: values.shippingAvailable || false,
          // backend-accepted key
          packageCounts: { "Non Ottoman": Number(finalPackageCount) || 1 },
          // NEW: Send per-size dimensions
          sizeDimensions: sizeDimensionsPayload,
          isInventoryPart: !!values.markInventoryPart,
          ...(Object.keys(priceMap).length ? { priceMap } : {}),
          ...(Object.keys(stockMap).length ? { stockMap } : {}),
          ...(userRole === "super admin" && Object.keys(costPriceMap).length
            ? { costPriceMap }
            : {}),
        };

        const resp = await axiosInstance.post("/admin/add-product", payload);

        if (resp.data.success) {
          const pid = resp.data.productId;
          createdProductIds.push(pid);

          if (fileList.length) {
            const fd = new FormData();
            fd.append("productId", pid);

            // ✅ SEND MAIN IMAGE INDEX
            fd.append("mainImageIndex", String(mainIndex));

            fileList.forEach((f) => {
              if (f.originFileObj) {
                fd.append("images", f.originFileObj);
              }
            });

            await axiosInstance.post("/admin/product/images", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          }

          if (values.createProduction) {
            try {
              await axiosInstance.post("/admin/production/create-from-product", {
                productId: pid,
                sizeIds,
                colorIds,
                stock: numericStock,
                packageCount: finalPackageCount,
              });
            } catch {
              message.warning("Product created, but sending to production failed.");
            }
          }
        } else {
          throw new Error("Failed to create product");
        }
      }

      message.success(`Successfully created ${createdProductIds.length} product(s)!`);

      // Optional: delete the draft if present (best-effort, don't fail publish)
      if (draftId) {
        try {
          await axiosInstance.delete(`/admin/drafts/${draftId}`);
        } catch (e) {
          // ignore
        }
      }

      setTimeout(() => navigate("/products"), 1500);
    } catch (error) {
      console.error("Error submitting products:", error);
      message.error("Error submitting the product(s).");
    } finally {
      setSubmitLoading(false);
    }
  };

  const stepFieldMap = [
    ["productImages", "productName", "category", "description"],
    ["sizes", "stock", "packageCount", "colors", "weight"],
    userRole === "super admin"
      ? ["price", "factory_price", "sku", "shippingAvailable", "duplicateCount", "markInventoryPart", "createProduction"]
      : ["price", "sku", "shippingAvailable", "duplicateCount", "markInventoryPart", "createProduction"],
  ];

  const goNext = async () => {
    const names = stepFieldMap[currentStep] || [];
    try {
      await form.validateFields(names);
      setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}
  };

  const goPrev = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const steps = [
    { title: "Images & Details", icon: <UploadOutlined /> },
    { title: "Sizes & Packaging", icon: <PlusOutlined /> },
    { title: "Pricing & Dimensions", icon: <CheckCircleOutlined /> },
  ];

  const responsiveSteps = steps.map((step) => ({
    ...step,
    title: window.innerWidth < 768 ? "" : step.title,
  }));

  // Table columns for sizes, prices, stock and dimensions
  const sizeTableColumns = [
    {
      title: "Variant",
      dataIndex: "size",
      key: "size",
      width: 120,
    },
    ...(userRole === "super admin"
      ? [
          {
            title: "Cost Price(£)",
            dataIndex: "id",
            key: "factory_price",
            width: 130,
            render: (id) => {
              const row = sizePriceRows[id] || {};
              return (
                <InputNumber
                  min={0}
                  value={row.cost !== "" ? Number(row.cost) : undefined}
                  onChange={(v) => updatePriceRow(id, { cost: v, _costTouched: true })}
                  style={{ width: "100%" }}
                  placeholder="0.00"
                  size="small"
                />
              );
            },
          },
        ]
      : []),
    {
      title: "Sale Price(€)",
      dataIndex: "id",
      key: "price",
      width: 130,
      render: (id) => {
        const row = sizePriceRows[id] || {};
        return (
          <InputNumber
            min={0}
            value={row.price !== "" ? Number(row.price) : undefined}
            onChange={(v) => updatePriceRow(id, { price: v, _priceTouched: true })}
            style={{ width: "100%" }}
            placeholder="0.00"
            size="small"
          />
        );
      },
    },
    {
      title: "Stock",
      dataIndex: "id",
      key: "stock",
      width: 110,
      render: (id) => {
        const row = sizePriceRows[id] || {};
        return (
          <InputNumber
            min={0}
            step={1}
            value={row.stock !== "" ? Number(row.stock) : undefined}
            onChange={(v) => updatePriceRow(id, { stock: v })}
            style={{ width: "100%" }}
            placeholder="0"
            size="small"
          />
        );
      },
    },
    {
      title: "Length (cm)",
      dataIndex: "id",
      key: "length",
      width: 120,
      render: (id) => {
        const dims = sizeDimensions[id] || {};
        return (
          <Input
            value={dims.length || ""}
            onChange={(e) => updateSizeDimension(id, "length", e.target.value)}
            placeholder="e.g., 200"
            size="small"
          />
        );
      },
    },
    {
      title: "Width (cm)",
      dataIndex: "id",
      key: "width",
      width: 120,
      render: (id) => {
        const dims = sizeDimensions[id] || {};
        return (
          <Input
            value={dims.width || ""}
            onChange={(e) => updateSizeDimension(id, "width", e.target.value)}
            placeholder="e.g., 150"
            size="small"
          />
        );
      },
    },
    {
      title: "Height (cm)",
      dataIndex: "id",
      key: "height",
      width: 120,
      render: (id) => {
        const dims = sizeDimensions[id] || {};
        return (
          <Input
            value={dims.height || ""}
            onChange={(e) => updateSizeDimension(id, "height", e.target.value)}
            placeholder="e.g., 30"
            size="small"
          />
        );
      },
    },
  ];

  const sizeTableData = sizes
    .filter((s) => selectedSizes.includes(s.size))
    .map((s) => ({
      key: s.id,
      id: s.id,
      size: s.size,
    }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <Spin spinning={submitLoading}>
        <Row gutter={[24, 24]} align="start">
          {/* LEFT: main form card */}
          <Col xs={24} lg={16}>
            <Card
              className="max-w-5xl w-full mx-auto bg-white rounded-2xl shadow-2xl border border-white/20"
              bodyStyle={{ padding: "32px 24px" }}
            >
              <div className="text-center mb-8">
                <Title level={2} className="!mb-2 !text-gray-800 font-bold">
                  Add New Product
                </Title>
                <Text type="secondary" className="text-gray-600 text-lg">
                  Step {currentStep + 1} of {steps.length} • Complete all steps to create your product
                </Text>
              </div>

              <div className="mb-10 px-4">
                <Steps current={currentStep} items={responsiveSteps} size="small" responsive={false} className="custom-steps" />
              </div>

              <Form 
                form={form} 
                layout="vertical" 
                onFinish={onFinish} 
                className="px-2"
                onValuesChange={(changedValues) => {
                  // Sync form changes with live preview state
                  if (changedValues.productName !== undefined) setProductName(changedValues.productName);
                  if (changedValues.description !== undefined) setDescription(changedValues.description);
                  if (changedValues.price !== undefined) setPrice(changedValues.price);
                  if (changedValues.factory_price !== undefined) setCostPrice(changedValues.factory_price);
                  if (changedValues.sku !== undefined) setSku(changedValues.sku);
                  if (changedValues.stock !== undefined) { setStock(changedValues.stock); }
                  if (changedValues.shippingAvailable !== undefined) setShippingAvailable(changedValues.shippingAvailable);
                  if (changedValues.duplicateCount !== undefined) setDuplicateCount(changedValues.duplicateCount);
                  if (changedValues.markInventoryPart !== undefined) setMarkInventoryPart(changedValues.markInventoryPart);
                  if (changedValues.priceGap !== undefined) setPriceGap(changedValues.priceGap || 0);

                  // if categories/sizes/colors changed, update selected states for preview + draft scheduling
                  if (changedValues.category !== undefined) {
                    setSelectedCategories(changedValues.category || []);
                  }
                  if (changedValues.sizes !== undefined) {
                    setSelectedSizes(changedValues.sizes || []);
                  }
                  if (changedValues.colors !== undefined) {
                    setSelectedColors(changedValues.colors || []);
                  }

                  // schedule draft save on any form change
                  scheduleDraftSave();
                }}
              >
                {currentStep === 0 && (
                  <div className="min-h-[500px] animate-fade-in">
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <PlusOutlined className="text-green-600 text-lg" />
                        </div>
                        <div>
                          <Text strong className="text-xl text-gray-800">Product Details</Text>
                          <Text type="secondary" className="block text-gray-600">
                            Product name & category on the left, images on the right
                          </Text>
                        </div>
                      </div>
                    </div>

                    <Row gutter={24} align="top">
                      <Col xs={24} lg={13}>
                        <Row gutter={16}>
                          <Col xs={24} md={11}>
                            <Form.Item
                              label={<span className="font-semibold text-gray-700">Product Name</span>}
                              name="productName"
                              rules={[{ required: true, message: "Enter the product name!" }]}
                            >
                              <Input
                                placeholder="Enter product name"
                                size="large"
                                className="rounded-lg border-gray-300 hover:border-blue-400 focus:border-blue-500"
                                onChange={(e) => setProductName(e.target.value)}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={11}>
                            <Form.Item
                              label={<span className="font-semibold text-gray-700">Category</span>}
                              name="category"
                              rules={[{ required: true, message: "Select at least one category!" }]}
                            >
                              <Select
                                mode="multiple"
                                placeholder="Select categories"
                                value={selectedCategories}
                                onChange={(vals) => {
                                  setSelectedCategories(vals);
                                  const hasMattress = vals.some((v) => isMattressName(v));
                                  setIsMattressSelected(hasMattress);
                                  if (hasMattress) {
                                    setPackageCount(1);
                                    form.setFieldsValue({ packageCount: 1 });
                                  }
                                  scheduleDraftSave();
                                }}
                                size="large"
                                className="rounded-lg"
                                dropdownRender={(menu) => {
                                  const visibleCategories = categories.filter(
                                    (c) => c.category_name.toLowerCase() !== "beds"
                                  );
                                  const allSelected =
                                    selectedCategories.length === visibleCategories.length;

                                  return (
                                    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                                      <div
                                        className="p-3 font-semibold cursor-pointer hover:bg-gray-50 rounded-t-lg flex justify-between items-center border-b border-gray-100"
                                        onClick={() => {
                                          const next = allSelected ? [] : visibleCategories.map((c) => c.category_name);
                                          setSelectedCategories(next);
                                          const hasMattress = next.some((v) => isMattressName(v));
                                          setIsMattressSelected(hasMattress);
                                          if (hasMattress) {
                                            setPackageCount(1);
                                            form.setFieldsValue({ packageCount: 1 });
                                          }
                                          scheduleDraftSave();
                                        }}
                                      >
                                        <span className="text-gray-700">Select All Categories</span>
                                        <Checkbox checked={allSelected} className="scale-110" />
                                      </div>
                                      <div className="max-h-64 overflow-y-auto">{menu}</div>
                                    </div>
                                  );
                                }}
                              >
                                {categories
                                  .filter((c) => c.category_name.toLowerCase() !== "beds")
                                  .map((c) => (
                                    <Option key={c.id} value={c.category_name} className="py-2">
                                      {c.category_name}
                                    </Option>
                                  ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row>
                          <Col span={22}>
                            <Form.Item
                              label={<span className="font-semibold text-gray-700">Description</span>}
                              name="description"
                              rules={[{ required: true, message: "Enter a description!" }]}
                            >
                              <ReactQuill
                                theme="snow"
                                value={description}
                                onChange={(val) => {
                                  setDescription(val);
                                  form.setFieldsValue({ description: val });
                                  scheduleDraftSave();
                                }}
                                modules={{
                                  toolbar: [
                                    ["bold", "italic", "underline"],
                                    [{ header: 1 }, { header: 2 }],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                    ["link", "image"],
                                    ["clean"]
                                  ]
                                }}
                                style={{ height: 300 }}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>

                      {/* RIGHT: Image Panel */}
                      <Col xs={24} lg={11} className="self-start lg:-mt-16 md:-mt-12 sm:-mt-10 -mt-8">
                        <div className="rounded-2xl bg-blue-50/50 p-4 border border-gray-200 gallery-uploader">
                          <div className="text-sm font-medium text-gray-700 mb-2">Upload Media</div>

                          {/* Main preview */}
                          <div className="main-preview">
                            {fileList.length > 0 ? (
                              <img
                                src={fileToUrl(fileList[mainIndex])}
                                alt={fileList[mainIndex]?.name || "preview"}
                                onClick={() => handlePreview(fileList[mainIndex])}
                              />
                            ) : (
                              <div className="placeholder" />
                            )}
                          </div>
{/* Thumbnails grid */}
<div className="thumbs-grid">
  {fileList.map((f, idx) => (
    <div key={f.uid || idx} className="thumb-wrapper">
      <button
        type="button"
        className={`thumb ${idx === mainIndex ? "active" : ""}`}
        onClick={() => setMainImage(idx)}
        title={f.name}
      >
        <img src={fileToUrl(f)} alt={f.name || `thumb-${idx}`} />
        
        {/* Professional Move Controls */}
        <div className="move-controls">
          {/* Move Left Button */}
          <button
            type="button"
            className={`move-btn left ${idx === 0 ? "disabled" : ""}`}
            disabled={idx === 0}
            onClick={(e) => {
              e.stopPropagation();
              moveImage(idx, idx - 1);
            }}
            aria-label="Move image left"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
            </svg>
          </button>
          
          {/* Move Right Button */}
          <button
            type="button"
            className={`move-btn right ${idx === fileList.length - 1 ? "disabled" : ""}`}
            disabled={idx === fileList.length - 1}
            onClick={(e) => {
              e.stopPropagation();
              moveImage(idx, idx + 1);
            }}
            aria-label="Move image right"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
          </button>
        </div>
        
        {/* Position Badge */}
        <div className="position-badge">
          {idx + 1}
        </div>
      </button>

      {/* Remove Button */}
      <button
        type="button"
        className="remove-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveImage(idx);
        }}
        aria-label="Remove image"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  ))}

  {fileList.length < MAX_IMAGES && (
    <Upload
      multiple
      fileList={fileList}
      onChange={handleImageChange}
      beforeUpload={beforeUploadValidate}
      accept="image/jpeg,image/png,image/webp"
      showUploadList={false}
    >
      <button type="button" className="thumb add-tile" title="Add image">
        <div className="plus-icon-wrapper">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </div>
      </button>
    </Upload>
  )}
</div>
                        </div>

                        <Modal
                          open={previewOpen}
                          title={previewTitle}
                          footer={null}
                          onCancel={() => setPreviewOpen(false)}
                        >
                          <Image alt={previewTitle} src={previewSrc} style={{ width: "100%" }} />
                        </Modal>
                      </Col>
                    </Row>

                    <style jsx>{`
                      .gallery-uploader { user-select: none; }
                      .main-preview {
                        width: 100%;
                        background: #fff;
                        border: 1px dashed #e5e7eb;
                        border-radius: 14px;
                        height: 260px;
                        display: flex; align-items: center; justify-content: center;
                        overflow: hidden;
                      }
                      .main-preview img {
                        max-width: 100%;
                        max-height: 100%;
                        object-fit: contain;
                        display: block;
                      }
                      .placeholder {
                        width: 100%;
                        height: 100%;
                        background: linear-gradient(180deg,#f8fafc,#eef2ff);
                      }
                    `}</style>
                  </div>
                )}

                {/* STEP 1: Sizes & Packaging */}
                {currentStep === 1 && (
                  <div className="min-h-[500px] animate-fade-in">
                    <div className="mb-8">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <PlusOutlined className="text-purple-600 text-lg" />
                        </div>
                        <div>
                          <Text strong className="text-xl text-gray-800">Sizes & Packaging</Text>
                          <Text type="secondary" className="block text-gray-600">
                            Select product sizes, colors and configure packaging
                          </Text>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item
                            label={<span className="font-semibold text-gray-700">Available Sizes</span>}
                            name="sizes"
                          >
                            <Select
                              mode="multiple"
                              placeholder="Select sizes"
                              value={selectedSizes}
                              onChange={(v) => {
                                setSelectedSizes(v);
                              }}
                              size="large"
                              className="rounded-lg w-full"
                              showSearch
                              optionFilterProp="children"
                              loading={loadingSizes}
                              dropdownRender={(menu) => {
                                const allSelected = selectedSizes.length === sizes.length;
                                return (
                                  <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                                    <div
                                      className="p-3 font-semibold cursor-pointer hover:bg-gray-50 rounded-t-lg flex justify-between items-center border-b border-gray-100"
                                      onClick={() => {
                                        const next = allSelected ? [] : sizes.map((s) => s.size);
                                        setSelectedSizes(next);
                                      }}
                                    >
                                      <span className="text-gray-700">Select All Sizes</span>
                                      <Checkbox checked={allSelected} className="scale-110" />
                                    </div>
                                    <div className="max-h-64 overflow-y-auto">{menu}</div>
                                  </div>
                                );
                              }}
                            >
                              {sizes.map((s) => (
                                <Option key={s.id} value={s.size} className="py-2">
                                  {s.size}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            label={<span className="font-semibold text-gray-700">Stock Quantity</span>}
                            name="stock"
                          >
                            <InputNumber
                              min={0}
                              step={1}
                              placeholder="Enter available stock"
                              size="large"
                              className="rounded-lg w-full"
                              parser={(value) => value.replace(/\D/g, "")}
                              onChange={(v) => { setStock(v || 0); scheduleDraftSave(); }}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            label={<span className="font-semibold text-gray-700">Colors</span>}
                            name="colors"
                          >
                            <Select
                              mode="multiple"
                              placeholder="Select colors"
                              size="large"
                              className="rounded-lg w-full"
                              value={selectedColors}
                              onChange={(vals) => {
                                setSelectedColors(vals);
                              }}
                              loading={loadingColors}
                              optionFilterProp="children"
                              showSearch
                            >
                              {colors.map((c) => (
                                <Option key={c.id} value={c.color_name} className="py-2">
                                  {c.color_name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item
                            label={<span className="font-semibold text-gray-700">Weight (kg)</span>}
                            name="weight"
                          >
                            <InputNumber
                              min={0}
                              step={0.1}
                              placeholder="Enter product weight"
                              size="large"
                              className="rounded-lg w-full"
                              parser={(value) => value.replace(/[^\d.]/g, "")}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            label={<span className="font-semibold text-gray-700">Number of Packages</span>}
                            name="packageCount"
                            initialValue={1}
                            rules={[{ required: true, message: "Enter number of packages!" }]}
                          >
                            <InputNumber
                              min={1}
                              value={packageCount}
                              onChange={(v) => {
                                setPackageCount(Number(v) || 1);
                                scheduleDraftSave();
                              }}
                              size="large"
                              className="rounded-lg w-full"
                            />
                          </Form.Item>
                          {isMattressSelected && <div className="mt-2" />}
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}

                {/* STEP 2: Pricing & Dimensions (with table moved here) */}
                {currentStep === 2 && (
                  <div className="min-h-[500px] animate-fade-in">
                    <div className="mb-8">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <PlusOutlined className="text-green-600 text-lg" />
                        </div>
                        <div>
                          <Text strong className="text-xl text-gray-800">Pricing & Dimensions</Text>
                          <Text type="secondary" className="block text-gray-600">
                            Set prices, dimensions per size, and how many listings to create
                          </Text>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <Row gutter={16}>
                        {userRole === "super admin" && (
                          <Col xs={24} md={8}>
                            <Form.Item
                              label={<span className="font-semibold text-gray-700">Base Cost Price (£)</span>}
                              name="factory_price"
                              tooltip="Base cost price for the first size"
                            >
                              <Input
                                prefix="£"
                                placeholder="0.00"
                                size="large"
                                className="rounded-lg border-gray-300 hover:border-blue-400 focus:border-blue-500"
                                onKeyPress={(e) => {
                                  if (!/[0-9.]/.test(e.key)) e.preventDefault();
                                  if (e.key === "." && e.currentTarget.value.includes(".")) e.preventDefault();
                                }}
                                onChange={(e) => { setCostPrice(e.target.value); scheduleDraftSave(); }}
                              />
                            </Form.Item>
                          </Col>
                        )}

                        <Col xs={24} md={userRole === "super admin" ? 8 : 12}>
                          <Form.Item
                            label={<span className="font-semibold text-gray-700">Base Sale Price (€)</span>}
                            name="price"
                            rules={[{ required: true, message: "Enter price in Euros!" }]}
                            tooltip="Base sale price for the first size"
                          >
                            <Input
                              prefix="€"
                              placeholder="0.00"
                              size="large"
                              className="rounded-lg border-gray-300 hover:border-blue-400 focus:border-blue-500"
                              onKeyPress={(e) => {
                                if (!/[0-9.]/.test(e.key)) e.preventDefault();
                                if (e.key === "." && e.currentTarget.value.includes(".")) e.preventDefault();
                              }}
                              onChange={(e) => { setPrice(e.target.value); scheduleDraftSave(); }}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={userRole === "super admin" ? 8 : 12}>
                          <Form.Item
                            label={<span className="font-semibold text-gray-700">SKU (Stock Keeping Unit)</span>}
                            name="sku"
                          >
                            <Input
                              placeholder="Enter unique SKU identifier"
                              size="large"
                              className="rounded-lg border-gray-300 hover:border-blue-400 focus:border-blue-500"
                              onChange={(e) => { setSku(e.target.value); scheduleDraftSave(); }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* SECOND ROW: Price Gap + Create Duplicate Listing */}
                      <Row gutter={16}>
                        {selectedSizes.length > 1 && (
                          <Col xs={24} md={8}>
                            <Form.Item
                              label={<span className="font-semibold text-gray-700">Price Gap (€)</span>}
                              name="priceGap"
                              initialValue={0}
                              tooltip="Automatic increment per next size"
                            >
                              <InputNumber
                                min={0}
                                step={1}
                                style={{ width: "100%" }}
                                value={priceGap}
                                onChange={(v) => { setPriceGap(Number(v) || 0); scheduleDraftSave(); }}
                                size="large"
                                className="rounded-lg"
                                placeholder="0"
                              />
                            </Form.Item>
                          </Col>
                        )}

                        <Col xs={24} md={8}>
                          <Form.Item
                            label={<span className="font-semibold text-gray-700">Create Duplicate Listing</span>}
                            name="duplicateCount"
                            initialValue={0}
                            rules={[
                              { type: "number", min: 0, max: 20, message: "Must be between 0-20" },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              max={20}
                              style={{ width: "100%" }}
                              placeholder="0"
                              size="large"
                              className="rounded-lg"
                              onChange={(v) => { setDuplicateCount(v || 0); scheduleDraftSave(); }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* NEW: Table for sizes, prices, and dimensions - MOVED TO HERE */}
                      {selectedSizes.length > 0 && (
                        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-800 bg-gray-50">
                            Configure Prices & Dimensions Per Size
                          </div>
                          <div className="overflow-x-auto">
                            <Table
                              dataSource={sizeTableData}
                              columns={sizeTableColumns}
                              pagination={false}
                              size="small"
                              bordered={false}
                              className="no-header-border"
                              scroll={{ x: 'max-content' }}
                            />
                          </div>
                        </div>
                      )}

                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item
                            name="markInventoryPart"
                            valuePropName="checked"
                            label={<span className="font-semibold text-gray-700">Add to Inventory</span>}
                            tooltip="Marks the created product(s) as inventory parts so they appear in the Inventory Parts list."
                          >
                            <Switch onChange={(v) => { setMarkInventoryPart(v); scheduleDraftSave(); }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 mx-4 mt-8 backdrop-blur-sm bg-white/95 action-bar-sticky">
                  <div className="flex justify-between items-center">
                    <Button
                      onClick={goPrev}
                      disabled={currentStep === 0}
                      icon={<ArrowLeftOutlined />}
                      size="large"
                      className="rounded-lg h-12 px-6 border-gray-300 text-gray-700 hover:text-gray-900 hover:border-blue-400"
                    >
                      Back
                    </Button>

                    {currentStep < steps.length - 1 ? (
                      <Button
                        type="primary"
                        onClick={goNext}
                        icon={<ArrowRightOutlined />}
                        size="large"
                        className="rounded-lg h-12 px-8 bg-gradient-to-r from-blue-500 to-indigo-600 border-0 shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        Next Step
                      </Button>
                    ) : (
                     <div className="flex gap-4 items-center">
  
  {/* Draft Status FIRST */}
  <div style={{ minWidth: 180 }}>
    {draftSaving ? (
      <Text type="secondary">Saving draft…</Text>
    ) : draftSavedAt ? (
      <Text type="secondary">
        Draft saved {new Date(draftSavedAt).toLocaleTimeString()}
      </Text>
    ) : (
      <Text type="secondary">No draft saved yet</Text>
    )}
  </div>

  {/* Save Draft Button */}
  <Button
    onClick={handleManualSaveDraft}
    loading={draftSaving}
    size="large"
    className="rounded-lg h-12 px-6 border-gray-300 text-gray-700 hover:text-gray-900"
  >
    Save Draft
  </Button>

  {/* Publish Button */}
  <Button
    type="primary"
    onClick={() => form.submit()}
    loading={submitLoading}
    size="large"
    className="rounded-lg h-12 px-8 bg-gradient-to-r from-green-500 to-emerald-600 border-0 shadow-lg hover:shadow-xl transition-all duration-300"
  >
    <CheckCircleOutlined className="mr-2" />
    Publish
  </Button>
</div>

                    )}
                  </div>
                </div>
              </Form>
            </Card>
          </Col>

          {/* RIGHT: Live Preview (sticky) */}
          <Col xs={24} lg={8}>
            <div className="hidden lg:block">
              <ProductLivePreview
                productName={productName}
                descriptionHTML={description}
                images={fileList}
                categories={selectedCategories}
                sizesSelected={selectedSizes}
                colorsSelected={selectedColors}
                price={price}
                isSuperAdmin={userRole === "super admin"}
                costPrice={costPrice}
                sku={sku}
                duplicateCount={duplicateCount}
                stock={stock}
                shippingAvailable={shippingAvailable}
                packageCount={packageCount}
                sizeDimensions={sizeDimensions}
                allSizes={sizes}
                sizePriceRows={sizePriceRows}
              />
            </div>

            <div className="lg:hidden">
              <ProductLivePreview
                productName={productName}
                descriptionHTML={description}
                images={fileList}
                categories={selectedCategories}
                sizesSelected={selectedSizes}
                colorsSelected={selectedColors}
                price={price}
                isSuperAdmin={userRole === "super admin"}
                costPrice={costPrice}
                sku={sku}
                duplicateCount={duplicateCount}
                stock={stock}
                shippingAvailable={shippingAvailable}
                packageCount={packageCount}
                sizeDimensions={sizeDimensions}
                allSizes={sizes}
                sizePriceRows={sizePriceRows}
              />
            </div>
          </Col>
        </Row>

        <style>{`
          .animate-fade-in { animation: fadeIn 0.5s ease-in-out; }
          @keyframes fadeIn { from { opacity:0; transform: translateY(10px);} to { opacity:1; transform: translateY(0);} }
          .custom-steps .ant-steps-item-title { font-weight: 600; }
          .action-bar-sticky { position: sticky; bottom: 20px; z-index: 10; }
          .no-header-border .ant-table-thead > tr > th { border-bottom: none; }

          /* Thumbnails Grid styles (kept from your original) */
          .thumbs-grid { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 16px; padding: 8px 0; }
          .thumb-wrapper { position: relative; width: 100px; height: 100px; }
          .thumb { position: relative; width: 100%; height: 100%; border: 2px solid #e5e7eb; border-radius: 8px; overflow: hidden; background: #f9fafb; transition: all 0.2s ease; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
          .thumb:hover { border-color: #3b82f6; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
          .thumb.active { border-color: #3b82f6; border-width: 3px; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
          .thumb img { width: 100%; height: 100%; object-fit: cover; }
          .move-controls { position: absolute; top: 50%; left: 0; right: 0; transform: translateY(-50%); display: flex; justify-content: space-between; padding: 0 4px; pointer-events: none; opacity: 0; transition: opacity 0.2s ease; }
          .thumb:hover .move-controls { opacity: 1; pointer-events: all; }
          .move-btn { width: 24px; height: 24px; border-radius: 4px; background: white; border: 1px solid #d1d5db; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s ease; pointer-events: all; color: #4b5563; padding: 0; }
          .move-btn:hover:not(.disabled) { background: #3b82f6; border-color: #3b82f6; color: white; transform: scale(1.1); }
          .move-btn.disabled { opacity: 0.3; cursor: not-allowed; }
          .position-badge { position: absolute; top: 4px; right: 4px; background: rgba(0, 0, 0, 0.7); color: white; font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 10px; min-width: 20px; text-align: center; backdrop-filter: blur(4px); }
          .remove-btn { position: absolute; top: -8px; right: -8px; width: 24px; height: 24px; border-radius: 50%; background: #ef4444; border: 2px solid white; color: white; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s ease; opacity: 0; padding: 0; z-index: 10; }
          .thumb-wrapper:hover .remove-btn { opacity: 1; }
          .remove-btn:hover { background: #dc2626; transform: scale(1.1); }
          .thumb { width: 96px; height: 96px; border-radius: 10px; overflow: hidden; display: flex; align-items: center; justify-content: center; background: #ffffff; }
          .add-tile { border: 2px dashed #d1d5db; background: #f8fafc; cursor: pointer; padding: 0; }
          .plus-icon-wrapper { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; }
          .plus-icon-wrapper svg { width: 28px; height: 28px; color: #9ca3af; transition: all 0.2s ease; }
          .add-tile:hover .plus-icon-wrapper svg { color: #3b82f6; transform: scale(1.1); }

        `}</style>
      </Spin>
      <Modal
      open={showDraftChoiceModal}
      closable={false}
      maskClosable={false}
      footer={null}
      centered
    >
      <div style={{ textAlign: "center" }}>
        <Title level={4}>You have an unfinished draft</Title>
        <Text type="secondary">
          Would you like to continue editing your latest draft?
        </Text>

        <div
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "center",
            gap: 16,
          }}
        >
          <Button
  type="primary"
  onClick={async () => {
    await loadDraftIntoForm(latestDraft.id);
    setAutoSaveEnabled(true);
  }}
>
  Continue Draft
</Button>
          <Button
  danger
  onClick={() => {
    setDraftId(null);
    setLatestDraft(null);
    setShowDraftChoiceModal(false);
    setAutoSaveEnabled(true); // IMPORTANT
    message.success("Starting new product");
  }}
>
  Start New Product
</Button>
        </div>
      </div>
    </Modal>
    </div>
  );
};

export default AddProductForm;
